/**
 * The roster: every company in the room, what it sells, what the cheapest
 * thing costs, and what nobody has looked at yet.
 *
 * The other tables assume this one. The landscape crosses two axes, the
 * feature matrix goes product by product — a plain "who is in this market"
 * was the one question you had to answer by working the filter rail. Rows
 * with nothing under them are the point rather than an embarrassment: an
 * empty row is a dig nobody has done, and the row says which one in words.
 * ONE dig button, at the top, for the table; a company you click into carries
 * its own on its page.
 */
import { useMemo } from 'react';
import TagCell from './TagCell';
import { CARD } from './tableLayout';
import type { BoardroomCompetitorRow, BoardroomCompetitors } from '../../../types';
import MissingList, { type OnDig } from './MissingList';
import DataTable from './DataTable';
import type { Column } from './tableColumns';
import { useReportSeen } from '../useSeen';
import NewHere from './NewHere';
import { arrivedAt, exactDate } from '../../common/relativeAge';

/** What a gap key means on a row, in the fewest words that still say it. */
const GAP_LABEL: Record<string, string> = {
  site: 'no website',
  company_page: 'home page not read',
  products: 'nothing recorded that it sells',
  features: 'products not read',
  features_empty: 'read, but nothing listed',
  pricing: 'no pricing',
};

/** ONE DESCRIPTION PER COLUMN: what it draws, what it means, how wide.
 *
 *  `value` is what the file gets and what sort compares. It used to live in a
 *  second, positional list inside the CSV shaper, which is how a reordered
 *  column could export the wrong data under the right heading.
 *
 *  The company column is `always`: a roster keyed by nothing is not a roster. */
export function competitorColumns(
  onOpen: (itemId: number) => void,
  /** Narrow the room to this tag. A tag you can SEE on a row and cannot act on
   *  is the lens's best entry point going unused: the landscape's axis headers
   *  have been clickable all along and these chips never were. */
  onPickTag?: (key: string) => void,
): Column<BoardroomCompetitorRow>[] {
  return [
    {
      key: 'company',
      label: 'company',
      width: 18,
      always: true,
      value: r => r.title,
      cell: r => (
        <>
          <button type="button" onClick={() => onOpen(r.id)} className="text-left font-medium text-gray-900 dark:text-gray-100 hover:underline">
            {r.title}
          </button>
          {r.verdict === 'HOLDS' && <span className="text-emerald-500" title="the fact checker stood behind this"> ✓</span>}
          {r.verdict === 'DOES_NOT_HOLD' && <span className="text-red-500" title="checked and did not hold"> ✗</span>}
          {/* Unread is not the same as having nothing: say which. */}
          {!r.read && <span className="ml-1 text-gray-400" title="nobody has read its pages">·unread</span>}
          {r.url && (
            <a href={r.url} target="_blank" rel="noreferrer" className="ml-1 text-gray-400 hover:text-gray-600" title={r.url}>
              ↗
            </a>
          )}
        </>
      ),
    },
    {
      key: 'sells',
      label: 'sells',
      width: 26,
      value: r => r.products.map(p => p.title).join(' / '),
      cell: r => (
        <>
          {r.products.length === 0 ? (
            <span className="text-gray-400">-</span>
          ) : (
            <span className="flex flex-wrap gap-x-1.5">
              {r.products.map(p => (
                <button key={p.id} type="button" onClick={() => onOpen(p.id)} className="hover:underline" title={p.solution_type?.title ?? undefined}>
                  {p.title}
                  {p.verdict === 'HOLDS' && <span className="text-emerald-500"> ✓</span>}
                </button>
              ))}
            </span>
          )}
          {r.features > 0 && <span className="text-gray-400"> · {r.features} feature{r.features === 1 ? '' : 's'}</span>}
        </>
      ),
    },
    {
      key: 'from',
      // "starts at", not "from": beside "tagged" the two headers read as one.
      label: 'starts at',
      width: 8,
      align: 'right',
      value: r => r.cheapest_label,
      cell: r => r.cheapest_label ?? <span className="text-gray-400">-</span>,
    },
    {
      // The other end of the range. A single price shows in both columns,
      // so a dash here would read as "no price".
      key: 'top',
      label: 'top price',
      width: 8,
      align: 'right',
      value: r => r.top_label ?? null,
      cell: r => r.top_label ?? <span className="text-gray-400">-</span>,
    },
    {
      key: 'tagged',
      label: 'tagged',
      width: 20,
      // Chips: real content, nothing to order by.
      sortable: false,
      value: r => r.tags.map(t => `${t.dimension.toLowerCase()}: ${t.value}`).join(' / '),
      // Grouped by kind, three kinds before "+N more" (see `TagCell`).
      cell: r => <TagCell tags={r.tags} onPickTag={onPickTag} />,
    },
    {
      // WHEN IT ARRIVED, so an insertion reads as an insertion.
      //
      // A row lighting up says something changed, but not whether the thing is
      // NEW or was merely touched — and a reader watching a dig wants to know
      // which. Sorting on this puts a company that has just appeared at the
      // top; one that was only updated stays where it was and still glows.
      key: 'added',
      label: 'added',
      width: 7,
      align: 'right',
      value: r => r.created_at,
      cell: r => (
        <span className="tabular-nums text-gray-500 dark:text-gray-400" title={exactDate(r.created_at)}>
          {arrivedAt(r.created_at)}
        </span>
      ),
    },
    {
      // WORDS, NOT A BUTTON PER ROW. Sixteen "dig into this table · 1" buttons
      // down one column is noise, and each was lying about its own scope.
      key: 'missing',
      label: 'what is missing',
      width: 28,
      // OFF UNTIL ASKED FOR: the line under the table counts the gaps and
      // fills them, and a column of them took a third of the width.
      hiddenByDefault: true,
      value: r => gapText(r.gaps),
      cell: r => <span className="text-amber-700 dark:text-amber-300">{gapText(r.gaps)}</span>,
    },
  ];
}

export function gapText(gaps: string[]): string {
  const seen = gaps.filter((g, i) => gaps.indexOf(g) === i);
  return seen.map((g) => GAP_LABEL[g] ?? g.replace(/_/g, ' ')).join(' · ');
}

export default function CompetitorsReport({
  data,
  onOpen,
  onDig,
  digging,
  unseen,
  onPickTag,
  onSeen,
  roomName,
}: {
  data: BoardroomCompetitors;
  onOpen: (itemId: number) => void;
  onDig: OnDig;
  digging: boolean;
  /** Ids that changed and have not been looked at. */
  unseen?: ReadonlySet<number>;
  /** Narrow the room to a tag a row is wearing. */
  onPickTag?: (key: string) => void;
  /** Ids now on screen — the caller drops them from the badge. */
  onSeen?: (ids: number[]) => void;
  roomName?: string;
}) {
  const columns = useMemo(() => competitorColumns(onOpen, onPickTag), [onOpen, onPickTag]);
  const loose = data.unattached ?? [];
  const shape = data.shape ?? [];
  // A room with loose products is not an empty room, even with no company yet.
  const empty = data.totals.companies === 0 && loose.length === 0;
  const unfinished = data.rows.filter(r => r.gaps.length > 0);
  const rows = data.rows;
  // ONE TRACKER, the roster and the questions under it. Two of them and the
  // list of questions pruned the roster's marks the moment the tab opened.
  const seen = useReportSeen(unseen, onSeen, [
    ...rows.map(r => r.id),
    ...data.missing.map(m => m.item_id),
  ]);

  return (
    <div className="flex flex-col gap-3 min-h-0">
      <NewHere count={seen.waitingHere} glowing={seen.glowing} onJump={seen.next} onClearAll={seen.readAll} />
      {!empty && (
        <div className="shrink-0 flex items-center gap-3 text-xs text-gray-600 dark:text-gray-300">
          <span className="ml-auto tabular-nums text-gray-400" data-testid="competitors-totals">
            {data.totals.companies} compan{data.totals.companies === 1 ? 'y' : 'ies'} · {data.totals.products} product
            {data.totals.products === 1 ? '' : 's'}
            {loose.length > 0 && <span className="text-amber-600 dark:text-amber-400"> ({loose.length} with no company)</span>}
            {' '}· {data.totals.read} read · {data.totals.checked} checked
            {/* What the checkbox used to count, as a number: the last column
                says which thing each one is missing. */}
            {unfinished.length > 0 && <> · {unfinished.length} with something missing</>}
          </span>
        </div>
      )}
      {!empty && (data.totals.axes ?? []).length > 0 && (
        /* HOW FAR A LENS CAN BE TRUSTED: companies sorted on each axis. Under
           half is amber — a lens on that axis leaves most of the market out. */
        <div className="shrink-0 -mt-2 text-right text-xs tabular-nums text-gray-400" data-testid="competitors-axes">
          sorted on{' '}
          {(data.totals.axes ?? []).map((a, i) => {
            const thin = data.totals.companies > 0 && a.tagged * 2 < data.totals.companies;
            return (
              <span key={a.dimension}>
                {i > 0 && ' · '}
                <span
                  className={thin ? 'text-amber-600 dark:text-amber-400' : undefined}
                  title={thin ? 'Fewer than half the companies are sorted on this; a lens on it leaves most out' : undefined}
                >
                  {a.dimension.toLowerCase().replace(/_/g, ' ')} {a.tagged}
                </span>
              </span>
            );
          })}{' '}
          of {data.totals.companies}
        </div>
      )}

      {!empty && (
        /* THE SAME GROUND AS EVERY OTHER REPORT. This was a bare div, so the
           table sat straight on the page — and the frozen first column, which
           paints its own opaque white so the rest can scroll under it, stood
           against grey with a seam down the left of every row. */
        <div className={CARD}>
          <DataTable
            table="competitors"
            room={roomName}
            seen={seen}
            columns={columns}
            rows={rows}
            rowKey={r => `competitor-${r.id}`}
            testId="competitors-table"
            empty="No companies yet."
          />
        </div>
      )}

      {/* PRODUCTS THAT HANG ON NOTHING. Every row above is reached through
          the company that sells it, so a product nobody filed under one was
          in no row and in no count — the table said 48 while the board held
          71, and the dig that found sixteen of them looked like it had done
          nothing. They get a block of their own rather than a quiet omission. */}
      {loose.length > 0 && (
        <div className="shrink-0" data-testid="competitors-unattached">
          <div className="text-xs text-amber-700 dark:text-amber-300 mb-1">
            No company recorded · {loose.length} product{loose.length === 1 ? '' : 's'}
            <span className="text-gray-400"> - nobody has said who makes {loose.length === 1 ? 'it' : 'them'}, so {loose.length === 1 ? 'it sits' : 'they sit'} under no row above</span>
          </div>
          <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs">
            {loose.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onOpen(p.id)}
                className="rounded border border-amber-200 dark:border-amber-800 px-1.5 py-0.5 text-gray-700 dark:text-gray-300 hover:underline"
                title={p.solution_type?.title ?? undefined}
              >
                {p.title}
                {p.verdict === 'HOLDS' && <span className="text-emerald-500"> ✓</span>}
                {!p.read && <span className="ml-1 text-gray-400">·unread</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* WHAT THE TABLES CANNOT REACH. A product under no company is invisible
          above; a feature under no product is invisible in the feature matrix.
          Both are the same defect, and neither shows up as a row anywhere —
          so the count is said out loud rather than left to be noticed. */}
      {shape.length > 0 && (
        <div className="shrink-0 text-xs text-gray-500 dark:text-gray-400" data-testid="competitors-shape">
          {shape.map((w) => (
            <span key={`${w.kind}:${w.what}`} className="mr-3">
              {w.count} of {w.of} {w.kind.toLowerCase()}
              {w.of === 1 ? '' : 's'}: {w.words}
            </span>
          ))}
        </div>
      )}

      <MissingList missing={data.missing} empty={empty} onOpen={onOpen} onDig={onDig} digging={digging} seen={seen} />
    </div>
  );
}
